import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api.js';
import DocPageThumb from '../components/DocPageThumb.jsx';
import InlineSvg from '../components/InlineSvg.jsx';
import { compressImage } from '../utils/image.js';
import { readLocal, writeLocal, removeLocal, readLocalJson, KEYS } from '../utils/storage.js';
import { friendlyError } from '../utils/errors.js';
import { newClientId, sendOutcome, awaitingReply, untilAnswered } from '../utils/turnSync.js';
import { buildDiagnosisSummary } from '../utils/diagnosisSummary.js';
import { setFeedbackContext } from '../utils/feedbackContext.js';
import { isDiagnosis, stripDiagnosisLabel } from '../utils/diagnosis.js';
import ManualLibraryLink from '../components/ManualLibraryLink.jsx';
import DocViewer from '../components/DocViewer.jsx';
import TurnRating from '../components/TurnRating.jsx';
import {
  READING_UNITS,
  describeExpect,
  expectLabel,
  judgeReading,
  parseTurnContent,
  buildReadingSentence,
} from '../../shared/turnMarkers.js';

// ─── Citation marker parsing ────────────────────────────────────────────────
//
// The grammar the AI emits ([doc:…], [svg]…[/svg], [expect …]) and everything
// that judges a reading against it now live in shared/turnMarkers.js, which
// the Worker imports too. They used to be a private copy in this file while
// the worker kept its own idea of the same grammar, and the two disagreed:
// the worker's [expect] guard was stripping every marker this file knows how
// to render, so the reading row below has never once appeared for a real tech.

/**
 * Walk every AI turn in a session, parse out [doc:N page:M] citations, and
 * return a deduplicated list ordered by first appearance. Powers the
 * "Reference materials cited" recap on the DIAGNOSIS turn so the tech can
 * pull up any page the AI referenced during the session without scrolling
 * the transcript.
 *
 * (We intentionally don't aggregate inline SVGs here — schematics are
 * step-specific and rarely useful out of context.)
 */
/**
 * Pull a clean hostname out of a URL for source-card subtitles. Falls back
 * to null on garbage input so we can render the raw URL instead.
 */
function hostFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function aggregateDocCitations(turns) {
  const seen = new Set();
  const out = [];
  for (const t of turns || []) {
    if (t.role !== 'ai' || !t.content) continue;
    const { visuals } = parseTurnContent(t.content);
    for (const v of visuals) {
      if (v.type !== 'doc') continue;
      const key = `${v.docId}-${v.page ?? 'all'}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ docId: v.docId, page: v.page });
    }
  }
  return out;
}

// One-tap answers shown above the answer composer once a session is in
// progress. Tapping one sends immediately — saves typing for the most
// common answers ("did you check X?" → "I haven't checked yet"). The
// AI is taught to handle these gracefully by the worker SYSTEM_PROMPT.
const QUICK_REPLIES = [
  'Yes',
  'No',
  "I haven't checked yet",
  "I don't know",
];

// "Best call" — asks the AI to commit to a diagnosis NOW, with a confidence
// line and an if-not alternative, instead of asking another question. The
// worker SYSTEM_PROMPT (CONVERGENCE section) keys on this wording. Shown only
// once the AI has asked a few questions: field sessions that ran past twenty
// turns never reached a diagnosis, and the tech had no way to force one.
const BEST_CALL_TEXT = "Give me your best call now — I need to wrap this up.";
const BEST_CALL_AFTER_QUESTIONS = 3;

// Persistence keys — KEYS.lastScan / KEYS.troubleshootSessionPrefix (see
// utils/storage.js), kept in sync with Result.jsx and Home.jsx.
//   KEYS.lastScan               lets Troubleshoot reconstruct `result` when
//                                the tech reaches this page via the bottom
//                                nav (which doesn't carry location state).
//   KEYS.troubleshootSessionPrefix  one entry per lookup_id, holding the
//                                active session id so a session survives
//                                page navigation. Cleared explicitly on
//                                End-session and Scan-another-tag.

function readLastScanResult() {
  const parsed = readLocalJson(KEYS.lastScan);
  return parsed?.result?.model_number ? parsed.result : null;
}

// Quick-pick symptoms shown above the symptom textarea — tapping one fills the
// box; the tech can still edit before sending. Keyed by appliance class so a
// dishwasher never offers "Not cooling" and a fridge never offers "Not
// draining" first. The class comes from the tag scan's free-text appliance
// type; the regexes mirror APPLIANCE_CLASSES in worker/routes/troubleshoot.js
// (order matters: "dishwasher" contains "washer").
const SYMPTOM_CLASSES = [
  ['microwave',     /microwave|micro-?wave|\bmwo\b|over[- ]the[- ]range/i],
  ['dishwasher',    /dish ?washer|dishwash/i],
  ['laundry',       /washer|dryer|laundry|washing machine|front[- ]load|top[- ]load/i],
  ['refrigeration', /refriger|fridge|freezer|ice ?maker|icemaker|wine|beverage|cooler/i],
  ['cooking',       /oven|range|cooktop|stove|broiler|burner|induction|warming drawer/i],
];
function symptomClass(applianceType) {
  for (const [cls, re] of SYMPTOM_CLASSES) if (re.test(applianceType || '')) return cls;
  return 'generic';
}
const QUICK_SYMPTOMS_BY_CLASS = {
  refrigeration: [
    { label: 'Not cooling',      value: 'Unit is not cooling.' },
    { label: 'Fresh food warm',  value: 'Freezer is cold but the fresh-food section is warm.' },
    { label: 'Too cold / freezing', value: 'Food is freezing in the fresh-food section.' },
    { label: 'Ice maker',        value: 'Ice maker is not making ice.' },
    { label: 'Leaking water',    value: 'Unit is leaking water.' },
    { label: 'Frost buildup',    value: 'Frost or ice is building up inside.' },
    { label: 'Noisy',            value: 'Unit is making an unusual noise.' },
    { label: 'Error code',       value: 'Unit is showing an error code: ' },
  ],
  dishwasher: [
    { label: 'Not draining',     value: 'Dishwasher is not draining.' },
    { label: 'Not filling',      value: 'Dishwasher is not filling with water.' },
    { label: 'Not cleaning',     value: 'Dishes come out dirty.' },
    { label: 'Not drying',       value: 'Dishes come out wet.' },
    { label: "Won't start",      value: 'Dishwasher will not start a cycle.' },
    { label: 'Leaking',          value: 'Dishwasher is leaking water.' },
    { label: 'Error code',       value: 'Dishwasher is showing an error code: ' },
    { label: 'Noisy',            value: 'Dishwasher is making an unusual noise.' },
  ],
  laundry: [
    { label: "Won't start",      value: 'Unit will not start / no power.' },
    { label: 'No heat',          value: 'Dryer runs but does not heat.' },
    { label: 'Not spinning',     value: 'Washer is not spinning or agitating.' },
    { label: 'Not draining',     value: 'Washer is not draining.' },
    { label: 'Takes too long',   value: 'Dryer takes too long to dry.' },
    { label: 'Leaking',          value: 'Unit is leaking water.' },
    { label: 'Noisy / vibration', value: 'Unit is noisy or vibrating.' },
    { label: 'Error code',       value: 'Unit is showing an error code: ' },
  ],
  cooking: [
    { label: 'Not heating',      value: 'Oven is not heating.' },
    { label: 'Temp off',         value: 'Oven temperature is wrong or uneven.' },
    { label: "Burner won't light", value: 'A burner will not light or stay lit.' },
    { label: 'Element out',      value: 'A cooktop element is not heating.' },
    { label: 'Door lock',        value: 'Oven door lock issue.' },
    { label: 'Display dead',     value: 'Display is dead or unresponsive.' },
    { label: 'Error code',       value: 'Unit is showing an error code: ' },
    { label: 'Self-clean',       value: 'Problem after or during self-clean.' },
  ],
  microwave: [
    { label: 'Not heating',      value: 'Microwave runs but does not heat.' },
    { label: 'No power',         value: 'Microwave is dead / no power.' },
    { label: 'Sparking',         value: 'Microwave is sparking / arcing inside.' },
    { label: 'Turntable',        value: 'Turntable is not turning.' },
    { label: 'Door / interlock', value: 'Door or interlock issue.' },
    { label: 'Noisy',            value: 'Microwave is making an unusual noise.' },
    { label: 'Display',          value: 'Display or keypad issue.' },
    { label: 'Error code',       value: 'Microwave is showing an error code: ' },
  ],
  generic: [
    { label: 'Not starting',     value: 'Unit will not start / no power.' },
    { label: 'Not heating',      value: 'Unit is not heating.' },
    { label: 'Not cooling',      value: 'Unit is not cooling.' },
    { label: 'Leaking',          value: 'Unit is leaking water.' },
    { label: 'Noisy',            value: 'Unit is making an unusual noise.' },
    { label: 'Error code',       value: 'Unit is showing an error code: ' },
    { label: 'Not draining',     value: 'Unit is not draining.' },
    { label: 'Door / latch',     value: 'Door or latch issue.' },
  ],
};

export default function Troubleshoot() {
  const { state } = useLocation();
  const navigate = useNavigate();
  // Prefer route state (passed from Result.jsx with full context). Fall back
  // to the persisted lastScan so the page works when the tech reaches it via
  // the bottom-nav after backgrounding the app, opening a different tab,
  // etc. — they should never have to re-scan to resume a job.
  const result = state?.result || readLastScanResult();

  // ── Session state ─────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(null);
  // The Feedback sheet attaches the session on screen (D-LAUNCH3).
  useEffect(() => {
    setFeedbackContext({ sessionId });
    return () => setFeedbackContext({ sessionId: null });
  }, [sessionId]);
  // This tech's existing 👍/👎 for the session, so the thumbs show what they
  // already chose after a resume. Best-effort: thumbs just start blank.
  const [ratings, setRatings] = useState({});
  useEffect(() => {
    setRatings({});
    if (!sessionId) return;
    let alive = true;
    api.myRatings(sessionId)
      .then((d) => {
        if (alive) setRatings(Object.fromEntries((d.ratings || []).map((r) => [r.turn_id, r])));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [sessionId]);
  const [turns, setTurns] = useState([]);     // [{turn_index, role, content, web_sources?}]
  // Where the AI's answers come from for THIS model, computed server-side:
  // 'manual' (indexed service manual), 'catalog' (doc on file, not indexed),
  // 'none' (no doc). Drives the grounding banner so a tech is never left
  // guessing whether a step is manufacturer-backed or general knowledge.
  const [grounding, setGrounding] = useState(null);
  const [done, setDone] = useState(false);    // AI emitted DIAGNOSIS:
  const [resolved, setResolved] = useState(false); // Tech ended the session
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Which action failed, so Retry can re-run it. Stores the action's NAME, not
  // a callback: a stored closure would capture the symptom/answer text as it
  // was at the moment of failure, and silently resend that after the tech
  // edited the box and tapped Retry. The one exception is a failed quick-reply
  // send, which stores { action: 'send', text } — the chip's text never lived
  // in the textarea, so a name-only retry would send nothing (or, worse,
  // whatever unrelated draft was sitting in the box).
  const [errorRetry, setErrorRetry] = useState(null);
  // Rehydrate failed on a transient error (network blip, worker 5xx). The
  // stored session id is kept — clearing it would sever the tech from a live
  // session — and the intake form stays hidden so a new session can't be
  // started on top of the existing one by accident.
  const [hydrateFailed, setHydrateFailed] = useState(false);
  const [hydrateNonce, setHydrateNonce] = useState(0);
  // True while the "end before diagnosis" confirm row is showing —
  // replaces window.confirm, which iOS standalone/home-screen PWAs can
  // silently no-op, risking a silent loss of the transcript.
  const [endConfirmPending, setEndConfirmPending] = useState(false);
  // True while the release-of-liability reminder is showing — the tech taps
  // "Start troubleshooting", this gate appears, and only "Customer has signed —
  // Continue" actually begins the session. Shown before EVERY new session; not
  // persisted (it's a reminder, not a recorded consent).
  const [waiverPending, setWaiverPending] = useState(false);
  // True while we're checking localStorage + worker for an existing session
  // for this scan. Suppresses the symptom-intake render so the tech doesn't
  // briefly see the "Start troubleshooting" form before the active session
  // appears underneath them.
  const [hydrating, setHydrating] = useState(true);
  // An unresolved session on this exact model from the last two days that
  // belongs to a different lookup (a re-scan makes a new lookup, so the
  // per-lookup session key finds nothing and the tech would start over).
  // Offered once; "Start fresh" dismisses it.
  const [resumeOffer, setResumeOffer] = useState(null);
  // What this company found on earlier calls for this model family. Shown
  // on the intake (from /context) and during the session (from the worker's
  // start/turn/get responses).
  const [priors, setPriors] = useState([]);

  // ── Document viewer ─────────────────────────────────────────────────────────
  // Tapping a manual-page thumbnail in the transcript opens that document in
  // the full-screen DocViewer, scrolled to the cited page. We look the doc up
  // by id in the model's document list (same source Result.jsx uses); the list
  // is fetched lazily and cached in docMapRef so repeated taps don't re-fetch.
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerPage, setViewerPage] = useState(1);
  const docMapRef = useRef(null);       // Map<id, doc> once loaded, else null
  const docsPromiseRef = useRef(null);  // in-flight fetch, so taps don't race

  // ── Inputs ────────────────────────────────────────────────────────────────
  const [symptom, setSymptom] = useState('');
  const [answer, setAnswer] = useState('');
  // Optional meter photo to attach to the next answer. Compressed data URL
  // ("data:image/jpeg;base64,..."). Sent with handleSend, then cleared.
  const [attachedPhoto, setAttachedPhoto] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  // Structured reading for a measurement turn: the number the tech read and
  // the unit. Prefilled from the last AI turn's [expect …] marker; judged
  // PASS / FAIL locally before it is sent, and sent as a plain sentence the
  // AI can read ("Reading: 12.4 Ω — PASS, expected 10–40 Ω").
  const [reading, setReading] = useState('');
  const [readingUnit, setReadingUnit] = useState('ohms');
  // Brief flag flipped true when the diagnosis was just copied — drives the
  // button label flash. Auto-clears after ~2s.
  const [justCopiedDiagnosis, setJustCopiedDiagnosis] = useState(false);
  // Was the diagnosis right? Asked once, after a DIAGNOSIS turn, before End
  // session. 'confirmed' | 'wrong' | 'unverified'. Stored on the session as
  // resolution_notes with a fixed prefix so it can be queried later — the only
  // signal that says whether a prompt change helped. Zero of the first 29
  // field sessions carried any such note.
  const [verdict, setVerdict] = useState(null);
  const [verdictNote, setVerdictNote] = useState('');
  // Bottom offset (px) for the fixed answer composer — 88 clears the bottom
  // nav when no keyboard is up; while the on-screen keyboard is open this
  // tracks its height via visualViewport so the composer sits just above it.
  const [composerBottom, setComposerBottom] = useState(88);
  // The fixed composer's real rendered height (px) — quick-reply chips wrap
  // to 1-3 rows and the photo preview grows/shrinks, so no fixed pixel
  // estimate is safe. Drives the transcript's bottom clearance below.
  const [composerHeight, setComposerHeight] = useState(0);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const transcriptRef = useRef(null);
  const answerRef = useRef(null);
  const composerRef = useRef(null);
  // Tracks whether the tech was already near the bottom of the transcript,
  // kept current by handleTranscriptScroll. Read (not recomputed from
  // scrollTop) by the auto-scroll effect below, since by the time that
  // effect runs the new turn has already grown scrollHeight out from under
  // the old scrollTop.
  const stickToBottomRef = useRef(true);
  // Per-mount sequence for optimistic tech-turn ids — turns.length isn't
  // collision-proof if two sends race before either commits state.
  const localTurnSeq = useRef(0);
  // Lost-reply recovery (D-REV13, src/utils/turnSync.js). A Start or answer
  // keeps its client id here until its outcome is known, so Try again, a
  // timeout or a Cancel resends with the SAME id and the worker hands back
  // what it stored instead of writing the turn twice.
  const startIdRef = useRef(null);
  const sendIdRef = useRef(null);
  // The in-flight request's AbortController (Cancel and End session use it),
  // and whether the tech pressed Cancel (to word the error).
  const inflightRef = useRef(null);
  const cancelledRef = useRef(false);
  // Answer text to resend after a resume that found a reply still owed.
  const pendingResumeRef = useRef(null);

  function handleTranscriptScroll(e) {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }

  // Auto-scroll to the newest turn after every render that adds turns — but
  // only when the tech hadn't already scrolled away from the bottom to
  // re-read an earlier answer or reference photo.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [turns.length, busy]);

  // The expected reading, if the AI's latest turn asked for a measurement.
  const lastAiTurn = [...turns].reverse().find((t) => t.role === 'ai');
  const expect = lastAiTurn && !isDiagnosis(lastAiTurn.content) ? parseTurnContent(lastAiTurn.content).expect : null;
  useEffect(() => {
    setReading('');
    if (expect?.unit) setReadingUnit(expect.unit);
  }, [lastAiTurn?.id, expect?.unit]);
  // readingUnit is passed deliberately: it is separate, tech-controlled state,
  // and without it 20 on the VAC chip judged PASS against a 10–40 Ω spec.
  const readingJudgement = expect && reading.trim()
    ? judgeReading(expect, reading, readingUnit)
    : { verdict: null, text: '' };

  // A new turn silences any step still being read aloud.
  useEffect(() => {
    try { window.speechSynthesis?.cancel(); } catch { /* not supported */ }
  }, [turns.length]);

  // Focus the answer box when the AI is waiting for one.
  useEffect(() => {
    if (sessionId && !done && !busy && answerRef.current) {
      answerRef.current.focus({ preventScroll: true });
    }
  }, [sessionId, done, busy, turns.length]);

  // Where the composer sits with no keyboard up: flush on top of the tab bar
  // (Layout's <nav data-tab-bar>, pinned to the screen bottom). Measured, not
  // guessed — a fixed 88 left a strip of transcript showing between the two
  // on phones, and the bar's height moves with the safe-area inset.
  function tabBarOffset() {
    const nav = document.querySelector('[data-tab-bar]');
    return nav ? Math.max(0, Math.round(window.innerHeight - nav.getBoundingClientRect().top)) : 88;
  }

  // Keep the answer composer above the iOS on-screen keyboard. visualViewport
  // shrinks (and its offsetTop grows) when the keyboard opens; window.
  // innerHeight does not — the gap between them is the keyboard inset.
  // Browsers without visualViewport keep the static 88px bottom-nav offset.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let keyboardWasOpen = false;
    function update() {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      const keyboardOpen = inset > 40;
      setComposerBottom(keyboardOpen ? inset : tabBarOffset());
      // Bring the latest turn back into view the moment the keyboard opens
      // rather than leaving it hidden behind the newly-risen composer.
      if (keyboardOpen && !keyboardWasOpen && transcriptRef.current) {
        transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
      }
      keyboardWasOpen = keyboardOpen;
    }
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Measure the fixed composer's real height so the transcript can reserve
  // exactly enough bottom clearance instead of a hardcoded guess — wrapped
  // chips + the photo preview can push it taller than any fixed estimate.
  // Re-runs on the same state that gates the composer's presence in the
  // JSX below, so it resets to 0 the moment the composer unmounts.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) {
      setComposerHeight(0);
      return;
    }
    const update = () => setComposerHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sessionId, done, resolved]);

  // Rehydrate an in-progress session for this scan. Runs once per
  // (lookup_id) — when the tech navigates back to Troubleshoot mid-session,
  // we look up the stored sessionId and pull the conversation back from
  // the worker. Resolved sessions don't rehydrate; the tech moves on to a
  // new symptom or scan.
  useEffect(() => {
    let cancelled = false;
    // A different scan (new lookup_id) must never inherit the prior model's
    // session, transcript, or grounding banner while we re-check for one.
    setSessionId(null);
    setTurns([]);
    setGrounding(null);
    startIdRef.current = null;
    sendIdRef.current = null;
    pendingResumeRef.current = null;
    setPriors([]);
    setResumeOffer(null);
    setDone(false);
    setHydrating(true);
    setHydrateFailed(false);
    // Drop any cached document list — it belongs to the previous model.
    docMapRef.current = null;
    docsPromiseRef.current = null;
    setViewerDoc(null);
    async function hydrate() {
      if (!result?.lookup_id) {
        setHydrating(false);
        return;
      }
      const key = `${KEYS.troubleshootSessionPrefix}${result.lookup_id}`;
      const storedId = readLocal(key);
      if (!storedId) {
        // No session for THIS lookup — but there may be one on the same unit
        // from a re-scan, and there may be history on the family. Both are
        // best-effort: a failure here just means the plain intake.
        try {
          const ctx = await api.getTroubleshootContext({ modelNumber: result.model_number, excludeLookupId: result.lookup_id });
          if (cancelled) return;
          setResumeOffer(ctx?.open_session || null);
          setPriors(ctx?.prior_diagnoses || []);
        } catch {
          /* intake works without it */
        }
        if (!cancelled) setHydrating(false);
        return;
      }
      try {
        const data = await api.getTroubleshootSession(storedId);
        if (cancelled) return;
        // If the worker says the session is already resolved, drop the
        // stored id — the tech finished it elsewhere (or in a prior
        // session) and shouldn't be dropped back into a closed transcript.
        if (data?.session?.resolved) {
          removeLocal(key);
          setHydrating(false);
          return;
        }
        setSessionId(Number(storedId));
        setTurns(data.turns || []);
        setGrounding(data.grounding || null);
        setPriors(data.prior_diagnoses || []);
        // If the last AI turn already contains a DIAGNOSIS:, surface the
        // diagnosis card so the tech sees it on resume instead of an
        // input box that won't lead anywhere.
        const lastAi = [...(data.turns || [])].reverse().find((t) => t.role === 'ai');
        setDone(!!lastAi && isDiagnosis(lastAi.content));
        // The conversation ends on the tech: the page was left (or Safari
        // suspended) while the AI was answering. Pick the reply up by
        // resending that answer with its id — the worker returns it once it
        // lands, and re-runs it if the first request died.
        const last = (data.turns || []).at(-1);
        if (awaitingReply(data.turns) && last?.client_turn_id && last.turn_index > 0) {
          sendIdRef.current = last.client_turn_id;
          pendingResumeRef.current = last.content;
        }
      } catch (e) {
        if (cancelled) return;
        if (e.status === 404 || e.status === 400 || e.status === 410) {
          // The server actually said the session is gone — clear and start fresh.
          removeLocal(key);
        } else {
          // Transient failure (network blip, worker 5xx): KEEP the stored id.
          // Deleting it here permanently severed the tech from a live
          // mid-diagnosis session over one dropped request.
          setHydrateFailed(true);
          setError(friendlyError(e, 'resume your session'));
          setErrorRetry('hydrate');
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    }
    hydrate();
    return () => { cancelled = true; };
  }, [result?.lookup_id, hydrateNonce]);

  // After a resume that found a reply still owed, fetch it (see hydrate).
  useEffect(() => {
    if (sessionId && !hydrating && pendingResumeRef.current) resumePendingReply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, hydrating]);

  async function resumePendingReply() {
    const text = pendingResumeRef.current;
    pendingResumeRef.current = null;
    if (!text || !sessionId || busy) return;
    setBusy(true);
    setError(null);
    setErrorRetry(null);
    const clientTurnId = sendIdRef.current;
    const controller = beginRequest();
    try {
      const data = await untilAnswered(() => api.sendTroubleshootTurn({
        sessionId,
        answer: text,
        clientTurnId,
        signal: controller.signal,
      }), { signal: controller.signal });
      if (superseded(controller)) return;
      sendIdRef.current = null;
      setTurns(data.turns);
      if (data.grounding) setGrounding(data.grounding);
      setDone(!!data.done);
    } catch (e) {
      if (superseded(controller)) return;
      if (sendOutcome(e) !== 'resend') sendIdRef.current = null;
      setError(sendErrorText(e, 'pick up the reply'));
      setErrorRetry({ action: 'resume', text });
    } finally {
      endRequest(controller);
    }
  }

  // Pick up the open session found by /context: store its id under THIS
  // lookup's key and re-run hydrate, which now finds it like any other resume.
  function resumeOpenSession() {
    if (!resumeOffer || !result?.lookup_id) return;
    writeLocal(`${KEYS.troubleshootSessionPrefix}${result.lookup_id}`, String(resumeOffer.id));
    setResumeOffer(null);
    setHydrateNonce((n) => n + 1);
  }

  // Re-run the rehydrate effect after a transient resume failure.
  function retryHydrate() {
    setError(null);
    setErrorRetry(null);
    setHydrateNonce((n) => n + 1);
  }

  // Escape hatch when resume keeps failing: give up on the stored session and
  // let the tech start fresh (their transcript stays in History).
  function abandonStoredSession() {
    if (result?.lookup_id) {
      removeLocal(`${KEYS.troubleshootSessionPrefix}${result.lookup_id}`);
    }
    setHydrateFailed(false);
    setError(null);
    setErrorRetry(null);
  }

  // ── No model selected guard ───────────────────────────────────────────────
  // Troubleshooting keys everything on the model number, so require one. A
  // No-Data-Tag unit scan can arrive with a null/UNKNOWN model — send the tech
  // to confirm the model rather than letting Start fail with a generic error.
  const hasModel = result && result.model_number && result.model_number !== 'UNKNOWN';
  if (!hasModel) {
    return (
      <div className="px-5 py-6 max-w-3xl mx-auto space-y-5">
        <div>
          <h1 className="h-display text-2xl text-white mb-1">Troubleshoot</h1>
          <p className="text-ink-400 text-sm">AI walks you through it, one step at a time.</p>
        </div>
        <div className="card border-marginal/30 bg-marginal/5">
          <p className="text-marginal font-medium mb-2">No model number yet</p>
          <p className="text-ink-300 text-sm mb-4">
            Troubleshooting needs the model number. Scan a data tag — or if you used
            “No Data Tag,” open the scan result and enter or confirm the model first.
          </p>
          <Link to="/" className="btn-primary">📷 Scan Tag</Link>
        </div>
      </div>
    );
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleStart() {
    const text = symptom.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    setErrorRetry(null);
    const clientSessionId = startIdRef.current || newClientId();
    startIdRef.current = clientSessionId;
    const controller = beginRequest();
    try {
      const data = await untilAnswered(() => api.startTroubleshoot({
        lookupId: result.lookup_id,
        modelNumber: result.model_number,
        brand: result.brand_matched || result.brand,
        applianceType: result.appliance_type,
        symptom: text,
        clientSessionId,
        signal: controller.signal,
      }), { signal: controller.signal });
      if (superseded(controller)) return;
      startIdRef.current = null;
      setSessionId(data.session_id);
      setTurns(data.turns);
      setGrounding(data.grounding || null);
      setPriors(data.prior_diagnoses || priors);
      setDone(!!data.done);
      // Persist for cross-page resume — the tech can leave Troubleshoot
      // (History, Scan Tag, etc.) and come back to the live conversation.
      writeLocal(`${KEYS.troubleshootSessionPrefix}${result.lookup_id}`, String(data.session_id));
    } catch (e) {
      if (superseded(controller)) return;
      // Only a refusal (4xx, the 503 budget) means nothing was stored; a
      // 409 here is the same Start racing itself, so resend that too.
      if (sendOutcome(e) === 'final') startIdRef.current = null;
      setError(sendErrorText(e, 'start the session'));
      setErrorRetry('start');
    } finally {
      endRequest(controller);
    }
  }

  // One AbortController per Start/answer, so Cancel and End session can stop
  // the wait. busy is cleared only by the request that set it.
  function beginRequest() {
    const controller = new AbortController();
    inflightRef.current = controller;
    cancelledRef.current = false;
    return controller;
  }
  function endRequest(controller) {
    if (inflightRef.current === controller) {
      inflightRef.current = null;
      setBusy(false);
    }
  }
  // True once End session (or a newer request) has replaced this one: its
  // late failure must not paint an error over the screen that replaced it.
  function superseded(controller) {
    return inflightRef.current !== controller;
  }
  function cancelInflight() {
    if (!inflightRef.current) return;
    cancelledRef.current = true;
    inflightRef.current.abort();
  }
  function sendErrorText(e, action) {
    if (cancelledRef.current) {
      return 'Stopped waiting. Try again picks the reply up if it arrived — your answer is never sent twice.';
    }
    return friendlyError(e, action);
  }

  async function handleSend(overrideText) {
    // Guards Cmd/Ctrl+Enter double-fire (key repeat / a double-tap) from
    // starting a second request against a session the worker rebuilds
    // statelessly from D1 on every call.
    if (busy) return;
    // A structured reading becomes the first sentence of the answer so the AI
    // sees the value, the unit, and the local PASS/FAIL in one place.
    //
    // This is NOT gated on overrideText. A quick-reply chip used to drop a
    // typed reading on the floor — the chip sent its own text, the reading
    // was never cleared, and the next turn's reset effect wiped it. The tech
    // measured, tapped "Yes", and the number was silently gone.
    const trimmedReading = reading.trim();
    const readingText = trimmedReading
      ? buildReadingSentence({ raw: trimmedReading, readingUnit, expect, judgement: readingJudgement })
      : '';
    const text = [readingText, (overrideText ?? answer).trim()].filter(Boolean).join(' ').trim();
    if (!text || !sessionId) return;
    setReading('');
    setBusy(true);
    setError(null);
    setErrorRetry(null);
    // Snapshot the photo to send + clear local state up-front so a re-tap
    // can't double-send the same image.
    const photoToSend = attachedPhoto;
    setAttachedPhoto(null);
    // Optimistically render the tech turn so the UI feels instant. Inline
    // photo data URL gets shown immediately; once the worker responds, the
    // server-rendered turn (with photo_r2_key) replaces this row. The id is
    // a per-mount sequence number, not turns.length, so two sends that race
    // before either commits state can't collide on the same React key.
    const optimistic = [
      ...turns,
      {
        id: `local-${++localTurnSeq.current}`,
        turn_index: turns.length,
        role: 'tech',
        content: text,
        // Local-only field used by the renderer to show the in-flight photo.
        local_photo_data_url: photoToSend,
      },
    ];
    setTurns(optimistic);
    setAnswer('');
    const clientTurnId = sendIdRef.current || newClientId();
    sendIdRef.current = clientTurnId;
    const controller = beginRequest();
    try {
      const data = await untilAnswered(() => api.sendTroubleshootTurn({
        sessionId,
        answer: text,
        photoDataUrl: photoToSend,
        clientTurnId,
        signal: controller.signal,
      }), { signal: controller.signal });
      if (superseded(controller)) return;
      sendIdRef.current = null;
      setTurns(data.turns);
      if (data.grounding) setGrounding(data.grounding);
      setDone(!!data.done);
      // The worker answered with an EARLIER send that had landed after all
      // (its response was lost). If the tech had typed something new since,
      // put it back so it isn't silently dropped.
      if (data.replayed) {
        const stored = (data.turns || []).find((t) => t.client_turn_id === clientTurnId);
        if (stored && stored.content !== text && overrideText == null) setAnswer(answer);
      }
    } catch (e) {
      if (superseded(controller)) return;
      const outcome = sendOutcome(e);
      if (outcome !== 'resend') sendIdRef.current = null;
      if (outcome === 'reload') {
        // Another send took this turn. Show the conversation as stored and
        // give the tech their words back to send next.
        try {
          const fresh = await api.getTroubleshootSession(sessionId);
          setTurns(fresh.turns || []);
          const lastAi = [...(fresh.turns || [])].reverse().find((t) => t.role === 'ai');
          setDone(!!lastAi && isDiagnosis(lastAi.content));
        } catch {
          setTurns(turns);
        }
        if (photoToSend) setAttachedPhoto(photoToSend);
        if (trimmedReading) setReading(trimmedReading);
        if (overrideText == null) setAnswer(answer);
        setError(friendlyError(e, 'send your answer'));
        return;
      }
      // Roll back the optimistic turn + restore the photo so the tech can retry.
      setTurns(turns);
      if (photoToSend) setAttachedPhoto(photoToSend);
      // Put the measured number back in the box. The send clears it up front
      // now (so a quick-reply chip carries it instead of dropping it), which
      // means a failure would otherwise leave the "Expected 10–40 Ω" pill on
      // screen above an empty reading row — the tech's measurement gone, and
      // nothing to suggest re-probing was unnecessary.
      if (trimmedReading) setReading(trimmedReading);
      // Restore typed text only if the failure came from the textarea path —
      // a quick-reply chip should not leave its own text in the box. Restore
      // the tech's OWN words, not `text`: `text` already has the reading
      // sentence prepended, and the retry recomposes it from live state.
      if (overrideText == null) setAnswer(answer);
      setError(sendErrorText(e, 'send your answer'));
      // Same reason the chip's retry carries only the chip's words: retrying
      // with the composed `text` prepends a SECOND reading sentence, and if the
      // tech re-probed and got a different number the model receives a PASS and
      // a FAIL for one measurement in one message.
      setErrorRetry(overrideText != null ? { action: 'send', text: overrideText } : 'send');
    } finally {
      endRequest(controller);
    }
  }

  // File-input change handler for the camera button. Compresses with the
  // existing image util (same path the data-tag scan uses) so payloads
  // stay reasonable on mobile networks. Resetting the input's value lets
  // the tech pick the same file twice in a row if needed.
  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    setError(null);
    try {
      const dataUrl = await compressImage(file, { maxDimension: 1280, quality: 0.85 });
      setAttachedPhoto(dataUrl);
    } catch (err) {
      setError('Could not read photo. Try again or skip.');
    } finally {
      setPhotoBusy(false);
    }
  }

  // Tap-to-send for the quick-reply chips above the composer. Bypasses the
  // textarea so the tech doesn't have to type "I haven't checked yet" by hand.
  function sendQuickReply(text) {
    if (busy || done || resolved) return;
    handleSend(text);
  }

  // Copies the diagnosis to the clipboard so the tech can paste it into the
  // work-order app or a customer-facing note. The text includes the model
  // and brand context so the paste is self-contained when it lands in
  // ServiceTitan / a phone note app.
  async function copyDiagnosis() {
    // Unit, complaint, readings taken and the diagnosis, markers stripped
    // (src/utils/diagnosisSummary.js) — the same text Share sends.
    const payload = buildDiagnosisSummary({ result, turns });
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      setJustCopiedDiagnosis(true);
      setTimeout(() => setJustCopiedDiagnosis(false), 2200);
    } catch (e) {
      setError('Could not copy. Long-press the diagnosis text to copy manually.');
    }
  }

  // Straight to Messages, Mail or AirDrop via the iPad's share sheet.
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  async function shareDiagnosis() {
    const text = buildDiagnosisSummary({ result, turns });
    if (!text) return;
    try {
      await navigator.share({ title: `Diagnosis · ${result.model_number}`, text });
    } catch (e) {
      // Closing the share sheet is not an error.
      if (e?.name !== 'AbortError') setError('Could not open sharing. Use Copy diagnosis instead.');
    }
  }

  // The verdict, as the fixed-prefix note stored on the session. Nothing when
  // the session ended before a diagnosis or the tech skipped the question.
  function buildResolutionNotes() {
    if (!done || !verdict) return null;
    const note = verdictNote.trim();
    if (verdict === 'confirmed') return 'CONFIRMED: diagnosis verified on site' + (note ? ` — ${note}` : '');
    if (verdict === 'wrong') return 'WRONG: ' + (note || 'diagnosis did not match what the tech found');
    return 'UNVERIFIED: tech ended the session before confirming the repair' + (note ? ` — ${note}` : '');
  }

  // Actually ends the session. Split out from handleEnd so a failed attempt
  // can be retried directly, without re-showing the confirm the tech
  // already got past.
  async function performEnd() {
    if (!sessionId) return;
    // End session stays tappable while a reply is pending; stop that wait so
    // its late answer doesn't land on a closed session.
    cancelInflight();
    inflightRef.current = null;
    sendIdRef.current = null;
    setBusy(true);
    setError(null);
    setErrorRetry(null);
    try {
      await api.endTroubleshoot({ sessionId, resolutionNotes: buildResolutionNotes() });
      setResolved(true);
      // Resolved sessions don't rehydrate. Drop the stored id so the tech
      // gets a fresh symptom intake on the next visit to this scan.
      if (result?.lookup_id) {
        removeLocal(`${KEYS.troubleshootSessionPrefix}${result.lookup_id}`);
      }
    } catch (e) {
      setError(friendlyError(e, 'close the session'));
      setErrorRetry('end');
    } finally {
      setBusy(false);
    }
  }

  // Mid-session presses (before the AI has reached DIAGNOSIS:) require a
  // confirm — saving a half-troubleshoot transcript loses context on a job
  // the tech is still working. Shows an inline confirm row rather than
  // window.confirm, which is unreliable in iOS standalone/home-screen PWA
  // contexts and could silently drop the confirm (and the transcript).
  // After diagnosis the bottom-card "End session & save" button skips the
  // confirm because the tech is making a deliberate completion choice.
  function handleEnd({ requireConfirm = false } = {}) {
    if (!sessionId) return;
    if (requireConfirm) {
      setEndConfirmPending(true);
      return;
    }
    performEnd();
  }

  // "Scan another tag" needs to clear the cached scan before navigating
  // to "/" — otherwise Home's persistent-scan effect (Session 2 fix)
  // redirects right back to the Result page. The cached scan lives under
  // the 'lastScan' key (see Result.jsx STORAGE_KEY and Home.jsx
  // LAST_SCAN_KEY). techName is intentionally NOT cleared — that's the
  // tech's persistent identity.
  function handleScanAnother() {
    removeLocal(KEYS.lastScan);
    // Also drop the troubleshoot session for this scan so the next
    // scan starts clean (the new lookup_id has its own key, but
    // pruning here keeps localStorage tidy).
    if (result?.lookup_id) {
      removeLocal(`${KEYS.troubleshootSessionPrefix}${result.lookup_id}`);
    }
    navigate('/');
  }

  // Load (once) the document list for this model and index it by id. Cached in
  // docMapRef; concurrent taps share the one in-flight promise.
  function loadDocMap() {
    if (docMapRef.current) return Promise.resolve(docMapRef.current);
    if (docsPromiseRef.current) return docsPromiseRef.current;
    const model = result?.model_number;
    if (!model) return Promise.resolve(null);
    docsPromiseRef.current = api
      .listDocumentsForModel(model)
      .then((data) => {
        const map = new Map((data.documents || []).map((d) => [d.id, d]));
        docMapRef.current = map;
        return map;
      })
      .catch(() => null)
      .finally(() => { docsPromiseRef.current = null; });
    return docsPromiseRef.current;
  }

  // Tap-to-open a cited manual page. Opens the in-app viewer at that page when
  // the doc is on file for this model; otherwise falls back to the raw file in
  // a new tab so the tech is never stuck on a dead thumbnail.
  async function openDoc(docId, page = 1) {
    const map = await loadDocMap();
    const doc = map?.get(docId);
    if (doc) {
      setViewerPage(page || 1);
      setViewerDoc(doc);
    } else {
      window.open(api.documentFileUrl(docId), '_blank', 'noopener');
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // The id of the newest AI turn — the one the tech hasn't answered yet (or,
  // once `done`, the one holding the diagnosis). Rating is only offered once
  // a turn is no longer the "live" question, so the thumbs can't be mistaken
  // for the Yes/No answer chips sitting right below it.
  const lastAiId = [...turns].reverse().find((t) => t.role === 'ai')?.id;

  // Keyboard: Cmd/Ctrl+Enter sends the answer. Guarded on `busy` so key
  // repeat / a double-tap on a Smart/Magic Keyboard can't fire two
  // concurrent sends.
  function answerKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (busy) return;
      handleSend();
    }
  }

  // The transcript is its own scroll container, and the fixed composer overlaps
  // its lower portion. So the clearance that lets the last message scroll above
  // the composer lives on the transcript itself (see its paddingBottom below),
  // not here — this outer padding just gives the page normal breathing room.
  return (
    <div className="px-5 py-6 max-w-3xl mx-auto space-y-4 pb-32">
      {/* Working-on header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-ink-400 text-xs font-mono uppercase tracking-widest mb-1">Working on</div>
            <div className="text-white font-mono text-lg font-bold break-all">{result.model_number}</div>
            <div className="text-ink-300 text-sm break-words">
              {(result.brand_matched || result.brand) || '—'}
              {result.appliance_type ? ` · ${result.appliance_type}` : ''}
            </div>
          </div>
          {sessionId && !resolved && (
            <button
              onClick={() => handleEnd({ requireConfirm: !done })}
              className="btn-ghost text-xs shrink-0"
              title="Mark this session resolved and save the transcript"
            >
              End session
            </button>
          )}
        </div>
      </div>

      {/* Inline "end before diagnosis" confirm — replaces window.confirm,
          which iOS standalone/home-screen PWAs can silently no-op. */}
      {endConfirmPending && (
        <div className="card border-marginal/30 bg-marginal/5">
          <p className="text-marginal font-medium mb-2">End before diagnosis?</p>
          <p className="text-ink-300 text-sm mb-4">
            The AI hasn't reached a diagnosis yet. The transcript will be saved as-is and you won't be able to send more answers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setEndConfirmPending(false)}
              className="btn-secondary flex-1"
            >
              Keep going
            </button>
            <button
              onClick={() => {
                setEndConfirmPending(false);
                performEnd();
              }}
              className="btn-primary flex-1"
            >
              End session
            </button>
          </div>
        </div>
      )}

      {/* While the page checks localStorage + the worker for an in-progress
          session, show a minimal placeholder instead of the symptom intake.
          Avoids the "you can start a new session" flash that would appear
          for a beat before the active session takes over. */}
      {hydrating && (
        <div className="card text-center text-ink-400">Loading session…</div>
      )}

      {/* Symptom intake — only before a session starts, and never while a
          stored session failed to resume (starting a new one on top of it
          would orphan the live conversation). */}
      {/* A session on this unit is still open from a re-scan: resume it
          instead of starting over. */}
      {!hydrating && !sessionId && !hydrateFailed && resumeOffer && (
        <div className="card border-accent/40 bg-accent/5">
          <p className="text-white font-semibold mb-1">A session on this unit is still open</p>
          <p className="text-ink-300 text-sm mb-3">
            {formatAgo(resumeOffer.created_at)}{resumeOffer.tech_name ? ` · ${resumeOffer.tech_name}` : ''} · {resumeOffer.turn_count} turns
            <br />
            <span className="text-ink-100">“{resumeOffer.symptom}”</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="button" onClick={resumeOpenSession} className="btn-primary flex-1">↩ Resume that session</button>
            <button type="button" onClick={() => setResumeOffer(null)} className="btn-secondary flex-1">Start fresh</button>
          </div>
        </div>
      )}

      {/* What this company found on earlier calls for this family — shown
          before the tech types a symptom, and kept above the transcript. */}
      {!hydrating && !hydrateFailed && priors.length > 0 && (
        <PriorDiagnoses items={priors} />
      )}

      {!hydrating && !sessionId && !hydrateFailed && (
        <SymptomIntake
          symptom={symptom}
          setSymptom={setSymptom}
          onStart={() => setWaiverPending(true)}
          busy={busy}
          modelNumber={result?.model_number}
          docCount={state?.docCount}
          applianceType={result?.appliance_type}
        />
      )}

      {/* Grounding banner — server-computed, so it can't be forgotten by the
          model mid-answer. Tells the tech whether steps are manufacturer-backed
          or general knowledge. */}
      {sessionId && grounding && <GroundingBanner grounding={grounding} brand={result?.brand_matched || result?.brand} />}

      {/* Transcript */}
      {sessionId && (
        <div
          ref={transcriptRef}
          onScroll={handleTranscriptScroll}
          className="space-y-3 max-h-[60vh] overflow-y-auto pr-1"
          // Reserve the fixed composer's height inside the scroll area so the
          // newest message can scroll clear of it instead of hiding behind it.
          style={{ paddingBottom: composerHeight ? composerHeight + 24 : 0 }}
        >
          {turns.map((t, i) => (
            <Turn
              key={t.id ?? `t-${i}`}
              turn={t}
              onOpenDoc={openDoc}
              rating={ratings[t.id]}
              ratable={typeof t.id === 'number' && (t.id !== lastAiId || done || resolved)}
            />
          ))}
          {busy && <Thinking onCancel={inflightRef.current ? cancelInflight : null} />}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="card border-fail/30 bg-fail/5">
          <p className="text-fail-400 mb-3">⚠ {error}</p>
          {errorRetry && (
            <button
              type="button"
              onClick={() => {
                if (errorRetry === 'start') handleStart();
                else if (errorRetry === 'end') performEnd();
                else if (errorRetry === 'hydrate') retryHydrate();
                else if (errorRetry?.action === 'send') handleSend(errorRetry.text);
                else if (errorRetry?.action === 'resume') {
                  pendingResumeRef.current = errorRetry.text;
                  resumePendingReply();
                }
                else handleSend();
              }}
              disabled={busy}
              className="btn-secondary w-full"
            >
              ↻ Try again
            </button>
          )}
          {errorRetry === 'hydrate' && (
            <button
              type="button"
              onClick={abandonStoredSession}
              className="btn-ghost w-full mt-2"
            >
              Start a new session instead
            </button>
          )}
        </div>
      )}

      {/* Answer composer — fixed bottom on mobile, inline on tablet. The
          bottom offset tracks the on-screen keyboard via composerBottom
          (see the visualViewport effect above) so iOS Safari can't cover
          it. The whole bar (chips + card) sits on one opaque bg-ink-900 —
          no see-through gaps between wrapped chip rows — and composerRef
          feeds the ResizeObserver above so the transcript reserves its
          real height instead of a guess. */}
      {sessionId && !done && !resolved && (
        <div
          ref={composerRef}
          className="fixed left-0 right-0 md:static md:mt-2 px-5 pt-3 pb-3 md:px-0 md:pt-0 md:pb-0 bg-ink-900 border-t border-ink-700 md:bg-transparent md:border-t-0"
          style={{ bottom: `${composerBottom}px` }}
        >
          <div className="max-w-3xl mx-auto">
            {/* Quick-reply chips — one tap sends. Sits above the composer
                so the tech sees them whenever the AI is waiting on an
                answer. Hidden while sending so back-to-back taps can't
                queue duplicates. */}
            <div className="flex flex-wrap gap-2 mb-2">
              {QUICK_REPLIES.map((reply) => (
                <button
                  key={reply}
                  type="button"
                  onClick={() => sendQuickReply(reply)}
                  disabled={busy}
                  className="inline-flex items-center justify-center min-h-[48px] px-3 py-2 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-lg text-sm border border-ink-700 active:scale-[0.97] transition disabled:opacity-50"
                >
                  {reply}
                </button>
              ))}
              {/* Force a diagnosis. Distinct styling so it reads as an exit,
                  not another answer. */}
              {turns.filter((t) => t.role === 'ai').length >= BEST_CALL_AFTER_QUESTIONS && (
                <button
                  type="button"
                  onClick={() => sendQuickReply(BEST_CALL_TEXT)}
                  disabled={busy}
                  title="Ask the AI to commit to its most likely diagnosis now"
                  className="inline-flex items-center justify-center min-h-[48px] px-3 py-2 bg-pass/10 hover:bg-pass/20 text-pass rounded-lg text-sm font-semibold border border-pass/40 active:scale-[0.97] transition disabled:opacity-50"
                >
                  🎯 Give me your best call
                </button>
              )}
            </div>
            <div className="card !p-3 bg-ink-900 border-ink-700">
              {/* Attached-photo preview — shown above the textarea so the
                  tech can see what they're about to send and remove it
                  with one tap if they grabbed the wrong frame. */}
              {attachedPhoto && (
                <div className="mb-2 flex items-start gap-2 p-2 bg-ink-800 border border-ink-700 rounded-lg">
                  <img
                    src={attachedPhoto}
                    alt="Meter reading"
                    className="w-16 h-16 object-cover rounded border border-ink-700 shrink-0"
                  />
                  <div className="flex-1 min-w-0 text-xs text-ink-300 leading-snug">
                    <div className="font-medium text-ink-100">📷 Photo attached</div>
                    <div className="text-ink-400">Will be sent with your next answer.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAttachedPhoto(null)}
                    aria-label="Remove photo"
                    className="shrink-0 w-11 h-11 inline-flex items-center justify-center text-ink-400 hover:text-white"
                  >
                    ×
                  </button>
                </div>
              )}
              <textarea
                ref={answerRef}
                value={answer}
                maxLength={2000}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={answerKeyDown}
                placeholder="Type what you saw / measured / heard…"
                rows={2}
                disabled={busy}
                className="w-full bg-transparent text-white placeholder-ink-500 focus:outline-none resize-none disabled:opacity-50"
              />
              {/* Reading row — only when the AI asked for a measurement. */}
              {expect && (
                <div className="mt-2 pt-2 border-t border-ink-800">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-ink-400 text-xs">{expectLabel(expect)}: {describeExpect(expect)}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reading}
                      onChange={(e) => setReading(e.target.value)}
                      placeholder="reading"
                      disabled={busy}
                      className="w-24 min-h-[48px] px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-white placeholder-ink-500 focus:outline-none focus:border-mra-red font-mono"
                    />
                    {/* 48px, not 40 — tailwind.config.js sets a 60pt floor for
                        gloved hands and these are the only composer controls
                        under it. They wrap in a flex row, so nothing shifts. */}
                    {Object.entries(READING_UNITS).map(([k, u]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setReadingUnit(k)}
                        disabled={busy}
                        className={[
                          'min-h-[48px] px-3 rounded-lg text-xs border active:scale-[0.97] transition disabled:opacity-50',
                          readingUnit === k ? 'bg-mra-red/15 border-mra-red text-mra-red font-semibold' : 'bg-ink-800 border-ink-700 text-ink-300',
                        ].join(' ')}
                      >
                        {u.label}
                      </button>
                    ))}
                    {/* OL was tappable mid-send while the input beside it was
                        disabled, so an OL entered during the round-trip landed
                        in a field the tech could not correct and was then wiped
                        by the reset effect. OL — open element, open thermal
                        fuse — is the reading techs enter most. */}
                    <button
                      type="button"
                      onClick={() => setReading('OL')}
                      disabled={busy}
                      className="min-h-[48px] px-3 rounded-lg text-xs border bg-ink-800 border-ink-700 text-ink-300 active:scale-[0.97] transition disabled:opacity-50"
                    >
                      OL
                    </button>
                    {readingJudgement.verdict && (
                      <span
                        className={[
                          'badge border',
                          readingJudgement.verdict === 'pass'
                            ? 'bg-pass/15 text-pass border-pass/30'
                            : readingJudgement.verdict === 'fail'
                              ? 'bg-fail/15 text-fail-400 border-fail/30'
                              : 'bg-marginal/15 text-marginal border-marginal/30',
                        ].join(' ')}
                      >
                        {readingJudgement.text}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <VoiceButton onTranscript={(t) => setAnswer((a) => (a ? a + ' ' + t : t))} />
                  <PhotoButton
                    onChange={handlePhotoChange}
                    busy={photoBusy}
                    hasPhoto={!!attachedPhoto}
                  />
                </div>
                <button
                  onClick={() => handleSend()}
                  disabled={busy || (!answer.trim() && !reading.trim())}
                  className="btn-primary"
                >
                  Send →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Diagnosis / end-of-session footer */}
      {sessionId && (done || resolved) && !busy && (
        <div className="card border-pass/30 bg-pass/5">
          <p className="text-pass font-semibold mb-3">
            {resolved ? '✓ Session saved' : '✓ Diagnosis ready'}
          </p>
          <p className="text-ink-300 text-sm mb-4">
            {resolved
              ? 'Saved to History. You can review the full transcript there.'
              : 'The AI has reached a conclusion. End the session to save it to History.'}
          </p>

          {/* Copy diagnosis to clipboard — saves the tech retyping the
              diagnosis into a work-order app. Includes the brand+model
              header so the pasted text is self-contained. */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button
              type="button"
              onClick={copyDiagnosis}
              className="flex-1 min-h-[48px] px-4 py-3 bg-pass/10 border border-pass/40 rounded-lg text-pass font-semibold active:scale-[0.99] transition"
            >
              {justCopiedDiagnosis ? '✓ Diagnosis copied' : '📋 Copy diagnosis'}
            </button>
            {canShare && (
              <button
                type="button"
                onClick={shareDiagnosis}
                className="flex-1 min-h-[48px] px-4 py-3 bg-ink-800 border border-ink-700 rounded-lg text-ink-100 font-semibold active:scale-[0.99] transition"
              >
                📤 Share diagnosis
              </button>
            )}
          </div>

          {/* Reference materials recap: every doc page the AI cited during
              the session, deduplicated. Lets the tech pull up the diagrams
              the AI used without scrolling back through the transcript. */}
          <ReferenceRecap turns={turns} onOpenDoc={openDoc} />

          {/* One question before the session closes. Two taps in the common
              case; a free-text line only when the call was wrong. */}
          {!resolved && (
            <div className="mb-4 pt-3 border-t border-pass/30">
              <p className="text-ink-100 font-semibold mb-2">Was the diagnosis right?</p>
              <div className="flex flex-wrap gap-2">
                {[
                  ['confirmed', '✓ Yes, confirmed'],
                  ['wrong', '✗ No, it was something else'],
                  ['unverified', 'Not verified yet'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setVerdict(key)}
                    className={[
                      'inline-flex items-center justify-center min-h-[48px] px-3 py-2 rounded-lg text-sm border active:scale-[0.97] transition',
                      verdict === key
                        ? 'bg-pass/20 border-pass text-pass font-semibold'
                        : 'bg-ink-800 border-ink-700 text-ink-200 hover:bg-ink-700',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {verdict === 'wrong' && (
                <input
                  type="text"
                  value={verdictNote}
                  onChange={(e) => setVerdictNote(e.target.value)}
                  placeholder="What was it actually? (part / cause)"
                  className="mt-3 w-full px-4 py-3 bg-ink-800 border border-ink-700 rounded-lg text-white placeholder-ink-500 focus:outline-none focus:border-mra-red"
                />
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {!resolved && (
              <button
                onClick={() => handleEnd()}
                disabled={busy || (done && !verdict)}
                title={done && !verdict ? 'Pick an answer above first' : undefined}
                className="btn-primary flex-1"
              >
                💾 End session &amp; save
              </button>
            )}
            <button onClick={handleScanAnother} className="btn-secondary flex-1">
              📷 Scan another tag
            </button>
          </div>
        </div>
      )}

      {/* Release-of-liability reminder — blocks starting a session until the
          tech confirms the customer has signed the waiver. */}
      {waiverPending && (
        <WaiverGate
          onContinue={() => {
            setWaiverPending(false);
            handleStart();
          }}
          onCancel={() => setWaiverPending(false)}
        />
      )}

      {/* Full-screen document viewer — opened by tapping a cited manual-page
          thumbnail in the transcript, scrolled to that page. */}
      {viewerDoc && (
        <DocViewer
          doc={viewerDoc}
          initialPage={viewerPage}
          onClose={() => setViewerDoc(null)}
        />
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

// Release-of-liability reminder shown before every troubleshooting session.
// The CUSTOMER signs an external/physical waiver — this only reminds the tech
// to obtain that signature before testing begins. It does NOT capture a
// signature or record consent; it's a gate the tech clears each session.
//
// PLACEHOLDER COPY — replace the wording below with the company's approved
// release-of-liability language when legal provides it.
function WaiverGate({ onContinue, onCancel }) {
  // Lock background scroll while the gate is up (same as DocViewer).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/95 flex items-center justify-center p-5"
      role="dialog"
      aria-modal="true"
      aria-label="Release of liability reminder"
    >
      <div className="card border-marginal/50 bg-ink-900 max-w-md w-full">
        <div className="flex items-start gap-3 mb-3">
          <span className="text-marginal text-2xl leading-none mt-0.5" aria-hidden="true">⚠</span>
          <div>
            <h2 className="text-white font-bold text-lg leading-tight">Release of Liability Required</h2>
          </div>
        </div>
        <p className="text-ink-200 text-sm leading-relaxed mb-2">
          Before testing this appliance, confirm the customer has read and signed the
          release-of-liability waiver.
        </p>
        <p className="text-ink-300 text-sm leading-relaxed mb-5">
          Do not begin diagnosis until the signed waiver is in hand.
        </p>
        <div className="flex flex-col gap-3">
          <button onClick={onContinue} className="btn-primary w-full">
            Customer has signed — Continue
          </button>
          <button onClick={onCancel} className="btn-secondary w-full">
            Back
          </button>
        </div>
      </div>
    </div>
  );
}

// "Seen before on this family": the DIAGNOSIS from earlier sessions on the
// same model family, with the tech's verdict when they gave one. Evidence
// for where to look first; the AI gets the same list in its prompt.
function PriorDiagnoses({ items }) {
  const badge = (v) =>
    v === 'confirmed'
      ? ['✓ confirmed', 'bg-pass/15 text-pass border-pass/30']
      : v === 'wrong'
        ? ['✗ overturned', 'bg-fail/15 text-fail-400 border-fail/30']
        : ['not verified', 'bg-ink-800 text-ink-400 border-ink-700'];
  return (
    <div className="card !py-3">
      <div className="text-ink-400 text-xs font-mono uppercase tracking-widest mb-2">
        Seen before on this family · {items.length}
      </div>
      <ul className="space-y-2">
        {items.map((p) => {
          const [label, cls] = badge(p.verdict);
          return (
            <li key={p.session_id} className="text-sm">
              <div className="flex flex-wrap items-center gap-2 text-ink-400 text-xs">
                <span>{formatAgo(p.created_at)}</span>
                <span className="font-mono">{p.model_number}</span>
                <span className={`badge border ${cls}`}>{label}</span>
              </div>
              <div className="text-ink-300">“{p.symptom}”</div>
              <div className="text-ink-100">→ {p.diagnosis}</div>
              {p.verdict === 'wrong' && p.notes && (
                <div className="text-fail-400 text-xs mt-0.5">Tech found: {p.notes.replace(/^WRONG:\s*/i, '')}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  const h = (Date.now() - d.getTime()) / 3_600_000;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
  if (h < 48) return `${Math.round(h)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Honest, always-visible source signal. The model can drop the "General
// guidance" label or skip a citation; this can't — it reads the DB's real
// grounding state for the model.
function GroundingBanner({ grounding, brand }) {
  const b = brand ? `${brand} ` : '';
  const cfg = {
    manual: {
      cls: 'border-pass/40 bg-pass/10 text-pass',
      icon: '📘',
      title: `Backed by the ${b}service manual`,
      sub: 'This model’s manual is on file and searched each step. Steps that pull from it show the page below.',
    },
    catalog: {
      cls: 'border-marginal/40 bg-marginal/5 text-marginal',
      icon: '⚠',
      title: 'General guidance — manual not indexed',
      sub: 'A manual is on file for this model but its pages aren’t readable yet, so steps are general repair knowledge plus a web lookup, not manufacturer spec. The AI won’t ask you to read it aloud — tell Jason so it can be indexed.',
    },
    none: {
      cls: 'border-marginal/40 bg-marginal/5 text-marginal',
      icon: '⚠',
      title: 'General guidance — no manual on file',
      sub: 'No service manual for this model yet. Steps are general repair knowledge; upload the tech sheet for model-specific specs.',
    },
  }[grounding];
  if (!cfg) return null;
  return (
    <div className={`card ${cfg.cls} !py-3`}>
      <div className="flex items-start gap-2">
        <span className="text-base leading-none mt-0.5" aria-hidden="true">{cfg.icon}</span>
        <div className="min-w-0">
          <div className="font-bold text-sm">{cfg.title}</div>
          <div className="text-ink-300 text-xs mt-0.5 leading-snug">{cfg.sub}</div>
        </div>
      </div>
    </div>
  );
}

function SymptomIntake({ symptom, setSymptom, onStart, busy, modelNumber, docCount, applianceType }) {
  const quickSymptoms = QUICK_SYMPTOMS_BY_CLASS[symptomClass(applianceType)];
  return (
    <div className="card">
      <h2 className="text-white font-bold text-tap mb-1">What's wrong with the unit?</h2>
      <p className="text-ink-400 text-sm mb-4">
        Describe the complaint, then I'll ask one question at a time.
      </p>

      {/* No tech sheet loaded → nudge the tech to pull one; a loaded manual
          gives the AI far better test points than a web search. Only shown
          when we know the count is zero (passed from Result), never guessed. */}
      {docCount === 0 && modelNumber && (
        <div className="mb-4">
          <p className="text-ink-400 text-sm mb-2">
            No tech sheet on file for this model — pulling one in gives a sharper diagnosis.
          </p>
          <ManualLibraryLink modelNumber={modelNumber} variant="secondary" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {quickSymptoms.map((q) => (
          <button
            key={q.label}
            onClick={() => setSymptom(q.value)}
            className="inline-flex items-center justify-center min-h-[48px] px-3 py-2 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-lg text-sm border border-ink-700 active:scale-[0.97] transition"
          >
            {q.label}
          </button>
        ))}
      </div>

      <textarea
        value={symptom}
        maxLength={1000}
        onChange={(e) => setSymptom(e.target.value)}
        placeholder="e.g. Dishwasher fills but doesn't start the wash cycle. No error code on the panel."
        rows={4}
        className="w-full px-4 py-3 bg-ink-800 border border-ink-700 rounded-lg text-white placeholder-ink-500 focus:outline-none focus:border-mra-red"
      />

      <button
        onClick={onStart}
        disabled={busy || !symptom.trim()}
        className="btn-primary w-full mt-4"
      >
        {busy ? '🔍 Thinking…' : '🔧 Start troubleshooting'}
      </button>
    </div>
  );
}

function Turn({ turn, onOpenDoc, rating, ratable }) {
  const isAi = turn.role === 'ai';
  const turnIsDiagnosis = isAi && isDiagnosis(turn.content);
  // Strip the leading DIAGNOSIS: token for nicer rendering — we already
  // visually mark these turns with a green border.
  const rawBody = turnIsDiagnosis ? stripDiagnosisLabel(turn.content) : turn.content;

  // Pull doc citations and inline SVGs out of the AI's text. Tech turns
  // never contain markers (humans don't speak in [doc:42]) so we still
  // run the parser unconditionally — the result is just empty for tech.
  const { textOnly, visuals, expect } = parseTurnContent(rawBody);

  // The model doesn't reliably emit [doc:N page:M], so the worker records the
  // manual pages it retrieved for this turn. Show them as thumbnails too,
  // deduped against anything the model DID cite, so the tech always sees the
  // relevant manual page.
  const citedKeys = new Set(
    visuals.filter((v) => v.type === 'doc').map((v) => `${v.docId}-${v.page ?? 1}`)
  );
  const retrievedThumbs = isAi
    ? (turn.retrieved_pages || [])
        .filter((p) => !citedKeys.has(`${p.document_id}-${p.page_number}`))
        .map((p) => ({ docId: p.document_id, page: p.page_number, key: `r-${p.document_id}-${p.page_number}` }))
    : [];

  return (
    <div
      className={[
        'rounded-2xl px-4 py-3',
        isAi
          ? turnIsDiagnosis
            ? 'bg-pass/10 border border-pass/30 text-ink-100'
            : 'bg-ink-900 border border-ink-700 text-ink-100'
          : 'bg-mra-red/10 border border-mra-red/30 text-ink-100 ml-8',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className={[
            'badge',
            isAi
              ? turnIsDiagnosis
                ? 'bg-pass/15 text-pass border border-pass/30'
                : 'bg-ink-800 text-ink-300 border border-ink-700'
              : 'bg-mra-red/15 text-mra-red border border-mra-red/30',
          ].join(' ')}
        >
          {isAi ? (turnIsDiagnosis ? 'Diagnosis' : 'AI') : 'You'}
        </span>
        {turn.used_web_search && <span className="badge-source">Web search</span>}
        {isAi && <ReadAloudButton text={textOnly} />}
      </div>

      <p className="whitespace-pre-wrap leading-relaxed">{textOnly}</p>

      {isAi && expect && (
        <div className="mt-2">
          <span
            className={`badge border bg-ink-800 border-ink-700 ${expect?.src === 'typical' ? 'text-ink-300 border-dashed' : 'text-ink-200'}`}
            title={expect?.src === 'typical' ? 'No spec for this in the manual on file — a typical value, so double-check before condemning a part.' : undefined}
          >📏 {expectLabel(expect)}: {describeExpect(expect)}</span>
        </div>
      )}

      {/* Meter photo on a tech turn — either the optimistic local data URL
          (still in flight) or the server's R2-backed URL after the round-
          trip. Clicking the thumb opens it in a new tab for full-screen
          inspection. */}
      {!isAi && (turn.local_photo_data_url || turn.photo_r2_key) && (
        <div className="mt-3">
          <a
            href={turn.local_photo_data_url || api.troubleshootPhotoUrl(turn.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <img
              src={turn.local_photo_data_url || api.troubleshootPhotoUrl(turn.id)}
              alt="Meter reading"
              className="max-h-40 rounded-lg border border-mra-red/40 object-contain bg-ink-950"
            />
          </a>
        </div>
      )}

      {/* Inline visuals — doc-page thumbnails and AI-generated SVG schematics.
          Renders as a wrapping flex row so a single thumb shows compact and
          two-or-three wrap nicely on the iPad portrait viewport. */}
      {visuals.length > 0 && (
        <div className="mt-3 flex flex-wrap items-start gap-3">
          {visuals.map((v) =>
            v.type === 'doc' ? (
              <DocPageThumb
                key={v.key}
                docId={v.docId}
                page={v.page || 1}
                onClick={onOpenDoc ? () => onOpenDoc(v.docId, v.page || 1) : undefined}
              />
            ) : (
              <InlineSvg key={v.key} markup={v.markup} />
            )
          )}
        </div>
      )}

      {/* Manual pages the worker retrieved for this step but the model didn't
          cite — surfaced so the tech can still pull up the real page. Labeled
          plainly as related reference, not as something the AI quoted. */}
      {retrievedThumbs.length > 0 && (
        <div className="mt-3 pt-3 border-t border-ink-800">
          <div className="text-ink-400 text-xs mb-2">📘 Manual pages related to this step</div>
          <div className="flex flex-wrap items-start gap-3">
            {retrievedThumbs.map((v) => (
              <DocPageThumb
                key={v.key}
                docId={v.docId}
                page={v.page}
                onClick={onOpenDoc ? () => onOpenDoc(v.docId, v.page) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {Array.isArray(turn.web_sources) && turn.web_sources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-ink-800">
          <p className="text-ink-400 text-xs uppercase tracking-wider mb-2">Sources</p>
          <div className="flex flex-col gap-2">
            {turn.web_sources.map((s, i) => {
              const host = hostFromUrl(s.url);
              return (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-ink-800 border border-ink-700 rounded-lg p-3 hover:border-mra-red transition active:scale-[0.99]"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-mra-red text-base leading-none mt-0.5">🔗</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-ink-100 font-medium text-sm">{s.title || host || s.url}</div>
                      {host && (
                        <div className="text-ink-400 text-xs truncate font-mono mt-0.5">{host}</div>
                      )}
                    </div>
                    <span className="text-ink-400 text-base leading-none mt-0.5 shrink-0">↗</span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Only an answered (no longer "live") AI turn is offered for rating —
          otherwise the thumbs sit right under the Yes/No answer chips and
          get tapped by mistake as an answer. Persisted AI turns only. */}
      {isAi && typeof turn.id === 'number' && ratable && <TurnRating turnId={turn.id} initial={rating} />}
    </div>
  );
}

/**
 * "Reference materials cited" block on the diagnosis card. Renders nothing
 * if no doc citations appeared in the session — diagnoses that were reached
 * purely from web search or model knowledge stay clean.
 */
function ReferenceRecap({ turns, onOpenDoc }) {
  const citations = aggregateDocCitations(turns);
  if (!citations.length) return null;
  return (
    <div className="mb-4 pt-3 border-t border-pass/30">
      <p className="text-ink-300 text-xs uppercase tracking-wider mb-3">
        Reference materials cited
      </p>
      <div className="flex flex-wrap items-start gap-3">
        {citations.map((c) => (
          <DocPageThumb
            key={`${c.docId}-${c.page ?? 'all'}`}
            docId={c.docId}
            page={c.page || 1}
            onClick={onOpenDoc ? () => onOpenDoc(c.docId, c.page || 1) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// Reads an AI step aloud with the browser's own voice — the prompt itself
// assumes a tech reading in a dim cabinet with hands in the machine. One
// utterance at a time; a new turn or a second tap stops it.
function ReadAloudButton({ text }) {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => () => { if (speaking) { try { window.speechSynthesis.cancel(); } catch { /* ignore */ } } }, [speaking]);
  if (!supported || !text) return null;
  function toggle() {
    const synth = window.speechSynthesis;
    if (speaking || synth.speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-US';
    u.rate = 1;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(u);
  }
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={speaking ? 'Stop reading' : 'Read this step aloud'}
      title={speaking ? 'Stop reading' : 'Read this step aloud'}
      className={[
        'ml-auto inline-flex items-center justify-center w-11 h-11 rounded-full border transition active:scale-95',
        speaking ? 'bg-mra-red text-ink-950 border-mra-red' : 'bg-ink-800 text-ink-300 border-ink-700 hover:text-white',
      ].join(' ')}
    >
      {speaking ? '■' : '🔊'}
    </button>
  );
}

// Cancel stops waiting on this device only; the worker may still finish the
// reply, and Try again (same client id) picks it up without a second turn.
function Thinking({ onCancel = null }) {
  return (
    <div className="rounded-2xl px-4 py-3 bg-ink-900 border border-ink-700">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-ink-500 rounded-full animate-pulse-soft" />
          <span className="w-2 h-2 bg-ink-500 rounded-full animate-pulse-soft" style={{ animationDelay: '0.2s' }} />
          <span className="w-2 h-2 bg-ink-500 rounded-full animate-pulse-soft" style={{ animationDelay: '0.4s' }} />
        </div>
        <span className="text-ink-400 text-sm flex-1">Thinking…</span>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-ghost text-sm min-h-[44px] px-3">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// File-input camera button. Uses native iOS / Android file picker with
// capture="environment" so the back camera opens directly. Cleaner than
// a full CameraCapture component for a quick meter snap — no framing
// guides, no preview screen, just snap and attach.
function PhotoButton({ onChange, busy, hasPhoto }) {
  const inputId = 'troubleshoot-photo-input';
  return (
    <>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        className="hidden"
        disabled={busy}
      />
      <label
        htmlFor={inputId}
        aria-label={hasPhoto ? 'Replace meter photo' : 'Attach meter photo'}
        className={[
          'inline-flex items-center justify-center w-12 h-12 rounded-full border transition active:scale-95 cursor-pointer',
          hasPhoto
            ? 'bg-mra-red/20 text-mra-red border-mra-red/50'
            : 'bg-ink-800 text-ink-300 border-ink-700 hover:text-white',
          busy ? 'opacity-50 cursor-wait' : '',
        ].join(' ')}
        title={hasPhoto ? 'Replace meter photo' : 'Attach meter photo'}
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      </label>
    </>
  );
}

// Web Speech API mic button — Safari/iPad supports it via webkitSpeechRecognition.
// Falls back to invisible if not available.
function VoiceButton({ onTranscript }) {
  const [supported] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  });
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const mountedRef = useRef(true);

  // Abort any live recognition session on unmount — otherwise the mic
  // stays hot and its callbacks call setState after the component is gone.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recRef.current?.abort();
    };
  }, []);

  if (!supported) return <span />;

  function toggle() {
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      if (!mountedRef.current) return;
      const t = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join(' ')
        .trim();
      if (t) onTranscript(t);
    };
    rec.onerror = () => { if (mountedRef.current) setListening(false); };
    rec.onend = () => { if (mountedRef.current) setListening(false); };
    rec.start();
    recRef.current = rec;
    setListening(true);
  }

  return (
    <button
      type="button"
      onClick={toggle}
      className={[
        'inline-flex items-center justify-center w-12 h-12 rounded-full border transition active:scale-95',
        listening
          ? 'bg-mra-red text-ink-950 border-mra-red animate-pulse-soft'
          : 'bg-ink-800 text-ink-300 border-ink-700 hover:text-white',
      ].join(' ')}
      title={listening ? 'Stop listening' : 'Dictate answer'}
      aria-label={listening ? 'Stop listening' : 'Dictate answer'}
    >
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    </button>
  );
}
